import React, { ReactNode, useState } from 'react';
import { IconButton, Tooltip, TooltipProps } from '@mui/material';
import { HighlightOff, InfoOutlined } from '@mui/icons-material';
import { useIsMobile } from 'hooks';

type InfoTooltipProps = { text: ReactNode; placement?: TooltipProps['placement'] };

const basicTooltipSlotProps: TooltipProps['slotProps'] = {
  arrow: {
    sx: {
      color: '#FFFFFF',
      '&::before': {
        boxShadow: '0px 0px 4px 0px rgba(0, 0, 0, 0.30)',
      },
    },
  },
  tooltip: {
    sx: {
      p: 4,
      background: '#FFFFFF',
      color: (theme) => theme.palette.text.primary,
      boxShadow: '0px 0px 4px 0px rgba(0, 0, 0, 0.30)',
      minWidth: { xs: '250px', md: '350px' },
    },
  },
};

const DesktopTooltip = ({ text, placement }: InfoTooltipProps) => {
  return (
    <Tooltip title={text} arrow placement={placement} slotProps={basicTooltipSlotProps}>
      <IconButton sx={{ p: 0 }} disableRipple>
        <InfoOutlined
          sx={{
            color: (theme) => theme.palette.grey[500],
            '&:hover': { color: (theme) => theme.palette.text.primary },
          }}
        />
      </IconButton>
    </Tooltip>
  );
};

const MobileTooltip = ({ text, placement }: InfoTooltipProps) => {
  const [open, setOpen] = useState(false);

  // setting high leaveTouchDelay allow us to achieve click-to-toggle functionality without ClickAwayListener
  return (
    <Tooltip
      leaveTouchDelay={240000}
      open={open}
      onClose={() => setOpen(false)}
      title={text}
      arrow
      placement={placement}
      slotProps={basicTooltipSlotProps}
    >
      <IconButton sx={{ p: 0 }} disableRipple onClick={() => setOpen(!open)}>
        {open ? (
          <HighlightOff sx={{ color: (theme) => theme.palette.text.primary }} />
        ) : (
          <InfoOutlined sx={{ color: (theme) => theme.palette.grey[500] }} />
        )}
      </IconButton>
    </Tooltip>
  );
};

export const InfoTooltip = ({ text, placement }: InfoTooltipProps) => {
  const { isMobile } = useIsMobile();

  return isMobile ? (
    <MobileTooltip text={text} placement={placement} />
  ) : (
    <DesktopTooltip text={text} placement={placement} />
  );
};
